import React, {useState, useEffect} from 'react';
import AjaxService from "../ajax/AjaxService";
import {MetronicInputText} from "../../metronic-components/MetronicInputText";
import UboldInvalidFeedbackList from "../../ubold-components/UboldInvalidFeedbackList";
import UboldAlert from "../../ubold-components/UboldAlert";
import {createInternationalizationPrefixer} from "../i18n/i18n";

const t = createInternationalizationPrefixer("RoleFormCard");

const PRIVILEGE_GROUPS = [
    {parent: 'PERM_SELECTION', childPrefix: 'PERM_SELECTION_'},
    {parent: 'PERM_HISTORY_AND_ISSUES', childPrefix: 'PERM_HISTORY_AND_ISSUES_'},
];

function getParentOf(value) {
    return PRIVILEGE_GROUPS.find(g => value.startsWith(g.childPrefix))?.parent ?? null;
}

function removeChildrenOf(parent, privileges) {
    const group = PRIVILEGE_GROUPS.find(g => g.parent === parent);
    if (!group) return privileges;
    return privileges.filter(p => !p.startsWith(group.childPrefix));
}

function parseLabel(label) {
    const m = label.match(/^([^(]+)\s*\((.+)\)$/s);
    return m ? {name: m[1].trim(), desc: m[2].trim()} : {name: label, desc: ''};
}

function PrivilegeCheckbox({option, checked, onChange}) {
    const {name, desc} = parseLabel(option.label);
    return (
        <div className="d-flex align-items-start gap-2 py-1">
            <input
                type="checkbox"
                className="form-check-input mt-1 flex-shrink-0"
                id={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
            />
            <label className="form-check-label" htmlFor={option.value}>
                <span>{name}</span>
                {desc && <small className="d-block text-muted">{desc}</small>}
            </label>
        </div>
    );
}

export default function RoleFormCard({formUrl, submitUrl, submitLabel, successHandler}) {
    const [name, setName] = useState('');
    const [privileges, setPrivileges] = useState([]);
    const [allOptions, setAllOptions] = useState([]);
    const [nameErrors, setNameErrors] = useState([]);
    const [privilegeErrors, setPrivilegeErrors] = useState([]);
    const [groupErrors, setGroupErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        async function load() {
            setLoading(true);
            const {json} = await AjaxService.getJson(formUrl);
            setName(json.value.name || '');
            setPrivileges(json.value.privileges || []);
            setAllOptions(json.configuration.configurations.privileges.options);
            setLoading(false);
        }
        load();
    }, [formUrl]);

    function togglePrivilege(value) {
        setPrivileges(prev => {
            if (prev.includes(value)) {
                return removeChildrenOf(value, prev.filter(p => p !== value));
            }
            const withValue = [...prev, value];
            const parent = getParentOf(value);
            if (parent && !withValue.includes(parent)) {
                return [...withValue, parent];
            }
            return withValue;
        });
        setPrivilegeErrors([]);
    }

    async function handleSubmit() {
        setSubmitting(true);
        const {json} = await AjaxService.postJson(submitUrl, {name, privileges});
        setSubmitting(false);
        const errors = json.errors;
        const editorErrors = errors.editorErrors || {};
        setGroupErrors(errors.wholeGroupLevelErrors || []);
        setNameErrors(editorErrors.name || []);
        setPrivilegeErrors(editorErrors.privileges || []);
        const hasErrors = (errors.wholeGroupLevelErrors || []).length > 0
            || (editorErrors.name || []).length > 0
            || (editorErrors.privileges || []).length > 0;
        if (!hasErrors) {
            successHandler();
        }
    }

    if (loading) {
        return <div className="text-center p-4 text-muted">{t('Ładowanie...')}</div>;
    }

    const selectionBase = allOptions.find(o => o.value === 'PERM_SELECTION');
    const selectionSubs = allOptions.filter(o => o.value.startsWith('PERM_SELECTION_'));
    const historyBase = allOptions.find(o => o.value === 'PERM_HISTORY_AND_ISSUES');
    const historySubs = allOptions.filter(o => o.value.startsWith('PERM_HISTORY_AND_ISSUES_'));
    const modernOptions = allOptions.filter(o =>
        o.value.startsWith('PERM_') &&
        !o.value.startsWith('PERM_SELECTION') &&
        !o.value.startsWith('PERM_HISTORY_AND_ISSUES')
    );
    const legacyOptions = allOptions.filter(o => !o.value.startsWith('PERM_'));

    const selectionGroupSection = (
        <div className="border rounded p-3 mb-3 bg-light">
            <div className="small fw-semibold text-secondary mb-2 text-uppercase">
                {t('Dobór wentylatorów')}
            </div>
            {selectionBase && (
                <PrivilegeCheckbox
                    option={selectionBase}
                    checked={privileges.includes(selectionBase.value)}
                    onChange={togglePrivilege}
                />
            )}
            <div className="ms-4 mt-1 border-start ps-3">
                {selectionSubs.map(opt => (
                    <PrivilegeCheckbox
                        key={opt.value}
                        option={opt}
                        checked={privileges.includes(opt.value)}
                        onChange={togglePrivilege}
                    />
                ))}
            </div>
        </div>
    );

    const historyGroupSection = (
        <div className="border rounded p-3 mb-3 bg-light">
            <div className="small fw-semibold text-secondary mb-2 text-uppercase">
                {t('Historia i sprawy')}
            </div>
            {historyBase && (
                <PrivilegeCheckbox
                    option={historyBase}
                    checked={privileges.includes(historyBase.value)}
                    onChange={togglePrivilege}
                />
            )}
            <div className="ms-4 mt-1 border-start ps-3">
                {historySubs.map(opt => (
                    <PrivilegeCheckbox
                        key={opt.value}
                        option={opt}
                        checked={privileges.includes(opt.value)}
                        onChange={togglePrivilege}
                    />
                ))}
            </div>
        </div>
    );

    const modernPrivilegesSection = (
        <div>
            {modernOptions.map(opt => (
                <PrivilegeCheckbox
                    key={opt.value}
                    option={opt}
                    checked={privileges.includes(opt.value)}
                    onChange={togglePrivilege}
                />
            ))}
        </div>
    );

    const legacyPrivilegesSection = legacyOptions.length > 0 && (
        <div>
            <hr className="my-3"/>
            <div className="small text-muted mb-2">{t('Legacy (przestarzałe nazwy)')}</div>
            {legacyOptions.map(opt => (
                <PrivilegeCheckbox
                    key={opt.value}
                    option={opt}
                    checked={privileges.includes(opt.value)}
                    onChange={togglePrivilege}
                />
            ))}
        </div>
    );

    return (
        <div>
            {groupErrors.map(err => (
                <UboldAlert key={err} severity="error" message={err}/>
            ))}
            <MetronicInputText
                label={t('Nazwa')}
                value={name}
                onChange={e => setName(e.target.value)}
                errors={nameErrors}
                placeholder={t('Nazwa roli np. Pracownik produkcji')}
                type="text"
            />
            <div className="row mb-4">
                <label className="col-12 col-md-4 col-form-label text-md-end">
                    {t('Uprawnienia')}
                </label>
                <div className="col-12 col-md-8">
                    {selectionGroupSection}
                    {historyGroupSection}
                    {modernPrivilegesSection}
                    {legacyPrivilegesSection}
                    <UboldInvalidFeedbackList errors={privilegeErrors}/>
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-8 offset-md-4">
                    <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? t('Zapisywanie...') : submitLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
